import type {
  ProgrammeRevisionDetailPublic,
  ProgrammeRevisionStatus,
} from "@common/client"
import { z } from "zod"
import type { ColumnSizing } from "@/lib/column-widths"
import type { GroupLabel } from "@/lib/grouping"
import {
  type SortValue,
  sortRows,
} from "@/routes/_layout/maintenance-programmes/-lib/sort"
import type { ProgrammeSearch } from "./search"
import { type Direction, withFixed } from "./table"

export type Revision = ProgrammeRevisionDetailPublic

export function revisionStatusLabel(status: ProgrammeRevisionStatus): string {
  switch (status) {
    case "draft":
      return "Draft"
    case "submitted":
      return "Submitted"
    case "released":
      return "Released"
    case "superseded":
      return "Superseded"
  }
}

export const REVISION_COLUMNS = [
  "revision",
  "date",
  "summary",
  "added",
  "removed",
] as const

export const revisionColumnSchema = z.enum(REVISION_COLUMNS)

export type RevisionColumn = z.infer<typeof revisionColumnSchema>

export type RevisionSlot = RevisionColumn | "status" | "actions"

export const REVISION_COLUMN_LABELS: Record<RevisionColumn, string> = {
  revision: "Revision",
  date: "Date",
  summary: "Summary",
  added: "Added",
  removed: "Removed",
}

export const REVISION_SIZING: ColumnSizing<RevisionSlot> = {
  id: "programme-revisions",
  widths: {
    revision: 6,
    status: 2,
    date: 6,
    summary: 20,
    added: 5,
    removed: 5.5,
    actions: 3.25,
  },
  fill: "summary",
  pinned: ["status", "actions"],
  extras: ["status", "actions"],
  dropOrder: ["date", "added", "removed", "summary"],
}

export const REVISION_GROUPINGS = ["status"] as const

export const revisionGroupingSchema = z.enum(REVISION_GROUPINGS)

export type RevisionGrouping = z.infer<typeof revisionGroupingSchema>

export const REVISION_GROUPING_LABELS: Record<RevisionGrouping, string> = {
  status: "Status",
}

export const REVISION_STATUSES = [
  "draft",
  "submitted",
  "released",
  "superseded",
] as const satisfies readonly ProgrammeRevisionStatus[]

export type RevisionFilterValues = { revision_status?: string[] }

export const NO_REVISION_FILTERS: RevisionFilterValues = {
  revision_status: undefined,
}

export type RevisionsDisplay = {
  columns: RevisionColumn[]
  sort: RevisionColumn
  direction: Direction
  group: RevisionGrouping | undefined
}

export function revisionsDisplayOf(search: ProgrammeSearch): RevisionsDisplay {
  return {
    columns: search.revision_columns
      ? withFixed(REVISION_COLUMNS, "revision", search.revision_columns)
      : [...REVISION_COLUMNS],
    sort: search.revision_sort ?? "date",
    direction: search.revision_direction ?? "desc",
    group: search.revision_group,
  }
}

export function revisionSlotsOf(columns: RevisionColumn[]): RevisionSlot[] {
  return columns
    .flatMap((column): RevisionSlot[] =>
      column === "revision" ? ["revision", "status"] : [column],
    )
    .concat("actions")
}

export function hasRevisionFilters(values: RevisionFilterValues): boolean {
  return Boolean(values.revision_status?.length)
}

function sortValue(revision: Revision, column: RevisionColumn): SortValue {
  switch (column) {
    case "revision":
      return revision.number
    case "date":
      return revision.effective_from ?? "9999"
    case "summary":
      return revision.summary
    case "added":
      return revision.added_count
    case "removed":
      return revision.removed_count
  }
}

export function shownRevisions(
  revisions: Revision[],
  display: RevisionsDisplay,
  filters: RevisionFilterValues,
): Revision[] {
  const statuses = filters.revision_status
  return sortRows(
    revisions.filter(
      (revision) => !statuses?.length || statuses.includes(revision.status),
    ),
    (revision) => sortValue(revision, display.sort),
    display.direction,
  )
}

export function revisionGroup(
  revision: Revision,
  grouping: RevisionGrouping,
): GroupLabel {
  switch (grouping) {
    case "status":
      return {
        key: revision.status,
        label: revisionStatusLabel(revision.status),
        order: REVISION_STATUSES.indexOf(revision.status),
      }
  }
}
